import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Crop as CropIcon, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { FileUpload } from "@/components/shared/FileUpload";
import {
  DEFAULT_SIZE_PT,
  JPG_PNG_ACCEPT,
  buildInvoiceLogoPreviewStyle,
  loadNaturalRatio,
  type CropFractions,
} from "@/components/features/company-image/invoiceLogoPreview";

const TILE_FRAME_PX = 96;

/**
 * Logo shown in the portal header/sidebar.
 *
 * Split out from the combined tiles because the two destinations turned out to
 * be different KINDS of setting: this one is what the tenant's own staff see in
 * the app, while the invoice tile is part of the commercial identity a client
 * is billed under — which is what a marca is. Settings renders them in
 * different cards for that reason; the onboarding surfaces still show both
 * together via LogoDestinationTiles.
 */
export function PortalLogoTile({
  portalLogoUrl,
}: {
  portalLogoUrl: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border p-4">
      <div className="text-sm font-semibold">
        {t("settings.companyImage.destinations.portalTitle")}
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {t("settings.companyImage.destinations.portalHelp")}
      </p>
      {/* This previews PORTAL CHROME, not a document, so it has to track
          the theme — a cream slab here told a dark-mode user their sidebar
          was cream. --fx-s-tile is the nested-surface token, so it stays
          separated from the card in both themes and in the card's hover. */}
      <div
        className="mt-3 flex items-center justify-center rounded-md bg-[var(--fx-s-tile)] p-3"
        style={{ height: TILE_FRAME_PX + 24 }}
      >
        {portalLogoUrl ? (
          // Round, matching how the sidebar/topbar actually render it.
          <img
            src={portalLogoUrl}
            alt=""
            className="h-16 w-16 rounded-full object-cover"
          />
        ) : (
          <span className="text-xs text-muted-foreground">
            {t("settings.companyImage.destinations.empty")}
          </span>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {t("settings.companyImage.destinations.portalNote")}
      </p>
    </div>
  );
}

export interface InvoiceLogoTileProps {
  /** Logo used on invoice PDFs (falls back to the portal one when shared). */
  invoiceLogoUrl: string | null;
  crop: CropFractions;
  /** True when one image serves both destinations. */
  shared: boolean;
  onSharedChange: (shared: boolean) => void;
  onAdjust: () => void;
  /** False when the parent cannot mount the crop dialog — hides the button
   *  instead of rendering one that silently does nothing. */
  canAdjust: boolean;
  onInvoiceLogoPicked: (file: File | null) => void;
  invoiceUploading: boolean;
  disabled: boolean;
  /**
   * Show the "usar una imagen distinta en la factura" checkbox.
   *
   * True in the COMBINED onboarding card, where one source image is being
   * pointed at two destinations and the toggle is the only way to say "not
   * this one for the invoice".
   *
   * False in Settings, where this tile edits ONE identity's logo: picking a
   * different image IS choosing a different image, and a checkbox in front of
   * the picker only asks the user to declare an intention they already
   * expressed by uploading. Going back to the company logo is a row action
   * there, the same one every marca has.
   */
  showSharedToggle?: boolean;
}

/** Logo, crop and size as they land on the invoice PDF. */
export function InvoiceLogoTile({
  invoiceLogoUrl,
  crop,
  shared,
  onSharedChange,
  onAdjust,
  canAdjust,
  onInvoiceLogoPicked,
  invoiceUploading,
  disabled,
  showSharedToggle = true,
}: InvoiceLogoTileProps): JSX.Element {
  const { t } = useTranslation();
  const [naturalRatio, setNaturalRatio] = useState<number | null>(null);

  // The preview needs the source aspect ratio; load it off-screen.
  useEffect(() => {
    let cancelled = false;
    if (!invoiceLogoUrl) {
      setNaturalRatio(null);
      return;
    }
    void loadNaturalRatio(invoiceLogoUrl).then((ratio) => {
      if (!cancelled) setNaturalRatio(ratio);
    });
    return () => {
      cancelled = true;
    };
  }, [invoiceLogoUrl]);

  const previewStyle = invoiceLogoUrl
    ? buildInvoiceLogoPreviewStyle(invoiceLogoUrl, crop, naturalRatio, TILE_FRAME_PX)
    : null;

  const isCropped = crop.w < 1 || crop.h < 1 || crop.x > 0 || crop.y > 0;
  const sizePct = Math.round((crop.sizePt / DEFAULT_SIZE_PT) * 100);

  return (
    <div className="rounded-lg border p-4">
      <div className="text-sm font-semibold">
        {t("settings.companyImage.destinations.invoiceTitle")}
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {t("settings.companyImage.destinations.invoiceHelp")}
      </p>
      {/* Picker FIRST: choose the image, then look at the result. The
          preview and crop control below describe whatever is selected
          here, so showing them above the picker inverted the reading
          order. The toggle lives in this tile because it only affects
          this destination; it is inverted so the checkbox names the
          exception (sharing one image is the common case). */}
      {showSharedToggle && (
        <div className="mt-3 flex items-center space-x-2">
          <Checkbox
            id="use-separate-invoice-logo"
            checked={!shared}
            onCheckedChange={(checked) => onSharedChange(checked !== true)}
            disabled={disabled}
            className="transition-colors duration-150"
          />
          <Label
            htmlFor="use-separate-invoice-logo"
            className="cursor-pointer text-xs font-normal"
          >
            {t("settings.companyImage.useSeparateInvoiceLogo")}
          </Label>
        </div>
      )}

      {(!showSharedToggle || !shared) && (
        <div className="mt-3">
          <FileUpload
            key={`invoice-${invoiceLogoUrl ?? "default"}`}
            currentImageUrl={invoiceLogoUrl}
            onFileChange={onInvoiceLogoPicked}
            disabled={disabled}
            showLabel={false}
            acceptedMimeTypes={JPG_PNG_ACCEPT}
          />
        </div>
      )}

      {/* Result of the above. This one previews a PDF PAGE, so it stays
          light in both themes — theming it would misrepresent the output.
          White, not cream: invoice-preview-pdf.service.ts never fills a
          page background, so the page is PDFKit's default white. The
          hairline stops a white slab floating on the dark card. */}
      <div
        className="mt-3 flex items-center justify-center rounded-md border border-[var(--fx-s-hairline)] bg-white p-3"
        style={{ height: TILE_FRAME_PX + 24 }}
      >
        {previewStyle ? (
          <div style={previewStyle} />
        ) : invoiceUploading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <span className="text-center text-xs text-muted-foreground">
            {t("settings.companyImage.destinations.empty")}
          </span>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {isCropped
            ? t("settings.companyImage.destinations.croppedAt", { pct: sizePct })
            : t("settings.companyImage.destinations.fullAt", { pct: sizePct })}
        </span>
        {invoiceLogoUrl && canAdjust && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={onAdjust}
          >
            <CropIcon className="mr-1.5 h-3.5 w-3.5" />
            {t("settings.companyImage.crop.open")}
          </Button>
        )}
      </div>
    </div>
  );
}

export interface LogoDestinationTilesProps extends InvoiceLogoTileProps {
  /** Logo shown in the portal header/sidebar. */
  portalLogoUrl: string | null;
}

/**
 * Both destinations side by side, with a live preview of each.
 *
 * The portal/invoice split previously existed only as a checkbox, so users had
 * to reason about which image went where and could not see that the invoice
 * applies a crop at all. Rendering both destinations makes the difference
 * observable instead of inferred.
 *
 * Still used by the onboarding surfaces, where one card covering both
 * destinations is the right amount of ceremony. Settings splits them: the
 * invoice half belongs with the marcas, since it IS the legal entity's marca.
 */
export function LogoDestinationTiles({
  portalLogoUrl,
  ...invoice
}: LogoDestinationTilesProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t("settings.companyImage.destinations.title")}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <PortalLogoTile portalLogoUrl={portalLogoUrl} />
        <InvoiceLogoTile {...invoice} />
      </div>
    </div>
  );
}
